import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { snack } from '~/hooks/ToastProvider';
import { SibillSocketChannel } from '~/sockets/sibill-socket';
import { useSibillSocket } from '~/state/notifications/socket';
import { BookkeepingAdjustmentTypeFilter } from '~/types/general-bookkeeping';
import { downloadUrl } from '~/utils/download';
import { clearExportingJournalPDF, setExportingJournalPDF } from './cash-book-state';

interface GeneralJournalReportReadyPayload {
  company_id: string;
  tracing_id: string;
  pdf_url: string;
}

/**
 * useGeneralJournalPDFExport
 *
 * Triggers async General Journal PDF generation via a dedicated WebSocket channel.
 *
 * Flow:
 * 1. Generates a UUID as `channel_id` and stores it in the Jotai atom (tracks loading state)
 * 2. Joins `general_journal_pdf:{channel_id}` — the join triggers PDF generation server-side
 * 3. On `BOOKKEEPING_GENERAL_JOURNAL_REPORT_READY`: clears atom + triggers browser download
 * 4. On `BOOKKEEPING_GENERAL_JOURNAL_REPORT_ERROR` or 90s timeout: clears atom + shows toast
 *
 * Loading state is exposed globally via `useIsExportingJournalPDF()` from cash-book-state.
 */
export const useGeneralJournalPDFExport = () => {
  const { t } = useTranslation('notifications');
  const { joinChannel } = useSibillSocket();
  const channelRef = useRef<SibillSocketChannel | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cleanup = (channelId: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (channelRef.current) {
      channelRef.current.leave();
      channelRef.current = undefined;
    }
    clearExportingJournalPDF(channelId);
  };

  const exportPDF = (
    companyId: string,
    from: string,
    to: string,
    adjustmentType: BookkeepingAdjustmentTypeFilter[],
  ) => {
    // Clean up any previous in-flight request
    if (channelRef.current) {
      channelRef.current.leave();
      channelRef.current = undefined;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    const channelId = crypto.randomUUID();

    setExportingJournalPDF(channelId);

    channelRef.current = joinChannel(`general_journal_pdf:${channelId}`, {
      company_id: companyId,
      from,
      to,
      adjustment_type: adjustmentType,
    });

    if (!channelRef.current) {
      clearExportingJournalPDF(channelId);
      snack.enqueue(
        { message: t('bookkeeping_general_journal_pdf_error.message'), type: 'error' },
        { timeout: 10_000 },
      );
      return;
    }

    channelRef.current.registerEvent(
      'BOOKKEEPING_GENERAL_JOURNAL_REPORT_READY',
      (payload: GeneralJournalReportReadyPayload) => {
        cleanup(channelId);
        downloadUrl(payload.pdf_url);
      },
    );

    channelRef.current.registerEvent('BOOKKEEPING_GENERAL_JOURNAL_REPORT_ERROR', () => {
      cleanup(channelId);
      snack.enqueue(
        { message: t('bookkeeping_general_journal_pdf_error.message'), type: 'error' },
        { timeout: 10_000 },
      );
    });

    // Safety timeout: 90 seconds
    timeoutRef.current = setTimeout(() => {
      cleanup(channelId);
      snack.enqueue(
        { message: t('bookkeeping_general_journal_pdf_error.message'), type: 'error' },
        { timeout: 10_000 },
      );
    }, 90_000);
  };

  return { exportPDF };
};
